import api from '../axios.config';
import { SpringPage } from '../../types/billing';

/**
 * One-time wallet top-up links (WalletTopUpController, /billing/my/topups).
 *
 * These answers are NOT wrapped in ApiResponse: the body is the resource itself,
 * like the rest of /billing/my/**. The wallet is credited later, and only by the
 * payment gateway's verified webhook; creating a link credits nothing.
 */
export const TOPUP_ENDPOINTS = {
  QUOTE: '/billing/my/topups/quote',
  LIST: '/billing/my/topups',
  ONE: (id: number | string) => `/billing/my/topups/${encodeURIComponent(String(id))}`,
};

export type TopUpState = 'PENDING' | 'OPEN' | 'PAID' | 'EXPIRED' | 'LINK_FAILED' | 'REVIEW';
export type TopUpInvoiceStatus = 'NOT_INVOICED' | 'RECEIPTED' | 'TAX_INVOICED';

/** What a top-up of `credit` would cost, or why it cannot be made (`refusal`). Stores nothing. */
export interface TopUpQuote {
  organizationId: string;
  currency: string | null;
  credit: number | null;
  taxTreatment: string | null;
  taxRate: number | null;
  tax: number | null;
  charge: number | null;
  taxNote: string | null;
  gateway: string | null;
  gatewayLabel: string | null;
  mode: string | null;
  issuerOrganizationId: string | null;
  minCredit: number | null;
  maxCredit: number | null;
  refusal: string | null;
  refusalStatus: number | null;
}

/** One top-up. `url` only while it can still be paid (PENDING/OPEN). Times are server UTC. */
export interface TopUpView {
  id: number;
  organizationId?: string;
  state: TopUpState;
  currency: string;
  credit: number;
  taxTreatment: string | null;
  taxRate?: number | null;
  tax: number;
  charge: number;
  taxNote?: string | null;
  gateway: string | null;
  mode: string | null;
  url?: string | null;
  expiresAt?: string | number | null;
  paidAt?: string | number | null;
  invoiceStatus?: TopUpInvoiceStatus | null;
  invoiceNumber?: string | null;
  taxDocumentNumber?: string | null;
  requestedVia?: string | null;
  createdAt?: string | number | null;
  credited: boolean;
}

export type Result<T> =
  | { success: true; data: T; status?: number }
  | { success: false; error: string; status?: number };

const failure = <T>(error: any, fallback: string): Result<T> => {
  const status: number | undefined = error?.response?.status;
  const message = error?.response?.data?.message;
  if (message && typeof message === 'string') return { success: false, error: message, status };
  const words: Record<number, string> = {
    400: 'That amount cannot be topped up',
    403: 'You do not have access to this',
    404: 'Not found',
    409: 'Online top-up is not available for this account',
    429: 'Too many payment links this hour; try again later',
    502: 'The payment gateway refused the link; try again',
  };
  return { success: false, error: (status && words[status]) || fallback, status };
};

const withoutBlank = (p: Record<string, any>) =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const walletTopupsService = {
  quote: async (amount: number, organizationId?: string): Promise<Result<TopUpQuote>> => {
    try {
      const r = await api.get(TOPUP_ENDPOINTS.QUOTE, { params: withoutBlank({ amount, organizationId }) });
      return { success: true, data: r.data, status: r.status };
    } catch (e) {
      return failure(e, 'Could not price the top-up');
    }
  },

  create: async (amount: number, organizationId?: string): Promise<Result<TopUpView>> => {
    try {
      const r = await api.post(TOPUP_ENDPOINTS.LIST, { amount }, { params: withoutBlank({ organizationId }) });
      return { success: true, data: r.data, status: r.status };
    } catch (e) {
      return failure(e, 'Could not create the payment link');
    }
  },

  list: async (organizationId?: string, page = 0, size = 10): Promise<Result<SpringPage<TopUpView>>> => {
    try {
      const r = await api.get(TOPUP_ENDPOINTS.LIST, { params: withoutBlank({ organizationId, page, size }) });
      return { success: true, data: r.data, status: r.status };
    } catch (e) {
      return failure(e, 'Could not load top-up history');
    }
  },

  get: async (id: number, organizationId?: string): Promise<Result<TopUpView>> => {
    try {
      const r = await api.get(TOPUP_ENDPOINTS.ONE(id), { params: withoutBlank({ organizationId }) });
      return { success: true, data: r.data, status: r.status };
    } catch (e) {
      return failure(e, 'Could not load the top-up');
    }
  },
};

export default walletTopupsService;
